import type { RouteStop } from '@/domains/routeStop/types/RouteStop';
import type { Shipment } from '@/domains/shipment/types/Shipment';
import {
  getAddressIdsFromCache,
  upsertAddresses,
} from '@/domains/address/repositories/addressRepository';
import { apiRequest } from '@/composables/useApi';
import { addressesEndpoints } from '@/services/api/apiConfig';
import type { ApiResponse } from '@/types/api';
import type { Address } from '@/domains/address';

/**
 * Extract address IDs from RouteStops.
 * @param routeStops Array of route stops
 * @returns Set of unique address IDs
 */
const extractAddressIdsFromRouteStops = (routeStops: RouteStop[]): Set<number> => {
  const addressIds = new Set<number>();

  for (const stop of routeStops) {
    if (stop.addressId != null && stop.addressId > 0) {
      addressIds.add(stop.addressId);
    }
  }

  return addressIds;
};

/**
 * Extract address IDs from Shipments.
 * Includes both destinationAddressId and scannedAddressId.
 * @param shipments Array of shipments
 * @returns Set of unique address IDs
 */
const extractAddressIdsFromShipments = (shipments: Shipment[]): Set<number> => {
  const addressIds = new Set<number>();

  for (const shipment of shipments) {
    if (shipment.destinationAddressId != null && shipment.destinationAddressId > 0) {
      addressIds.add(shipment.destinationAddressId);
    }
    if (shipment.scannedAddressId != null && shipment.scannedAddressId > 0) {
      addressIds.add(shipment.scannedAddressId);
    }
  }

  return addressIds;
};

/**
 * Service for address-related business logic.
 * Handles orchestration of address loading and caching.
 */
export const addressService = {
  /**
   * Load missing addresses from RouteStops and/or Shipments.
   * Extracts address IDs, checks cache, and loads only missing addresses.
   * Fire-and-forget pattern - doesn't block the caller.
   */
  loadMissingAddressesFromEntities: async (
    routeStops?: RouteStop[],
    shipments?: Shipment[]
  ): Promise<void> => {
    try {
      // Extract all address IDs
      const addressIds = new Set<number>();

      // From route stops
      if (routeStops && routeStops.length > 0) {
        const routeStopAddressIds = extractAddressIdsFromRouteStops(routeStops);
        routeStopAddressIds.forEach((id) => addressIds.add(id));
      }

      // From shipments
      if (shipments && shipments.length > 0) {
        const shipmentAddressIds = extractAddressIdsFromShipments(shipments);
        shipmentAddressIds.forEach((id) => addressIds.add(id));
      }

      // If no address IDs found, nothing to do
      if (addressIds.size === 0) {
        return;
      }

      // Check which addresses are already in cache
      const cachedAddressIds = await getAddressIdsFromCache();
      const missingIds = Array.from(addressIds).filter((id) => !cachedAddressIds.has(id));

      // If all addresses are already in cache, nothing to do
      if (missingIds.length === 0) {
        return;
      }

      // Fetch missing addresses from API
      const endpoint = addressesEndpoints.BY_IDS(missingIds);
      const response = await apiRequest<ApiResponse<Address[]>>({
        endpoint,
        method: 'get',
      });

      if (response?.data && response.data.length > 0) {
        // Upsert the fetched addresses
        await upsertAddresses(response.data);
      }
    } catch (error) {
      // Log error but don't throw - this is fire-and-forget
      console.warn('[addressService] Failed to load addresses:', error);
    }
  },
};
